import { initializeFirebase } from "config/firebase";
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type MessagePayload,
} from "firebase/messaging";
import { NotificationsDeviceRegister } from "./http-requests/notifications";

const readEnv = (key: string): string =>
  process.env[`REACT_APP_FIREBASE_${key}`] ??
  process.env[`VITE_FIREBASE_${key}`] ??
  "";

const DEVICE_ID_KEY = "pushDeviceId";

const getOrCreateDeviceId = (): string => {
  const existing = localStorage.getItem(DEVICE_ID_KEY);
  if (existing) {
    return existing;
  }

  const generated =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `web-${Date.now()}`;
  localStorage.setItem(DEVICE_ID_KEY, generated);
  return generated;
};

type PushRegistrationResult = {
  ok: boolean;
  message: string;
  fcmToken?: string;
  step:
    | "permission"
    | "messaging_instance"
    | "vapid_key"
    | "service_worker"
    | "token"
    | "backend_register";
};

export const isDeviceRegisteredForPush = (): boolean => {
  return Boolean(localStorage.getItem("fcmToken"));
};

const getMessagingInstance = async () => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }

  const supported = await isSupported();
  if (!supported) {
    return null;
  }

  const app = initializeFirebase();
  if (!app) {
    return null;
  }

  return getMessaging(app);
};

export const registerDeviceForPush =
  async (): Promise<PushRegistrationResult> => {
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        return {
          ok: false,
          step: "permission",
          message: `Notification permission not granted (status: ${permission}).`,
        };
      }

      const messaging = await getMessagingInstance();
      if (!messaging) {
        return {
          ok: false,
          step: "messaging_instance",
          message:
            "Firebase messaging is not available (unsupported browser, missing service worker support, or Firebase not initialized).",
        };
      }

      const vapidKey = readEnv("VAPID_KEY");
      if (!vapidKey) {
        return {
          ok: false,
          step: "vapid_key",
          message:
            "Missing Firebase VAPID key. Set REACT_APP_FIREBASE_VAPID_KEY (or VITE_FIREBASE_VAPID_KEY).",
        };
      }

      let serviceWorkerRegistration: ServiceWorkerRegistration;
      try {
        serviceWorkerRegistration = await navigator.serviceWorker.register(
          "/firebase-messaging-sw.js"
        );
      } catch (error) {
        return {
          ok: false,
          step: "service_worker",
          message: `Failed to register firebase messaging service worker: ${
            error instanceof Error ? error.message : "Unknown error"
          }`,
        };
      }

      let fcmToken: string;
      try {
        fcmToken =
          (await getToken(messaging, {
            vapidKey,
            serviceWorkerRegistration,
          })) ?? "";
      } catch (error) {
        return {
          ok: false,
          step: "token",
          message: `Failed to fetch FCM token: ${
            error instanceof Error ? error.message : "Unknown error"
          }`,
        };
      }

      if (!fcmToken) {
        return {
          ok: false,
          step: "token",
          message:
            "FCM token is empty. Check Firebase config, VAPID key, and browser permissions.",
        };
      }

      try {
        const deviceId = getOrCreateDeviceId();
        await NotificationsDeviceRegister({
          DATA: {
            fcmToken,
            platform: "web",
            deviceId,
          },
        });
      } catch (error) {
        return {
          ok: false,
          step: "backend_register",
          message: `Device register API failed: ${
            error instanceof Error ? error.message : "Unknown error"
          }`,
          fcmToken,
        };
      }

      localStorage.setItem("fcmToken", fcmToken);
      return {
        ok: true,
        step: "backend_register",
        message: "Device registered successfully for push notifications.",
        fcmToken,
      };
    } catch (error) {
      return {
        ok: false,
        step: "messaging_instance",
        message: `Unexpected registration error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      };
    }
  };

type NotificationListenerOptions = {
  onNotification?: (payload: MessagePayload) => void;
  onNotificationClick?: (url: string) => void;
};

// Unified listener that fires for BOTH foreground and background pushes,
// so the UI (bell badge, etc.) stays in sync regardless of tab focus.
//
//  Foreground:  firebase/messaging `onMessage` fires for the active tab.
//  Background:  the service worker's `onBackgroundMessage` posts
//               { type: "FCM_BACKGROUND_MESSAGE" } to all open tabs.
//  Click:       the SW also posts { type: "FCM_NOTIFICATION_CLICK", url } when
//               the user clicks the OS notification, so the page can navigate.
//
// We `await navigator.serviceWorker.ready` before subscribing — without this,
// the page may add its `message` listener before the SW takes control, and
// the very first background push gets missed.
//
// Returns a single cleanup function; call it on component unmount.
export const startNotificationListener = async (
  options: NotificationListenerOptions
): Promise<() => void> => {
  const cleanupFns: Array<() => void> = [];

  const messaging = await getMessagingInstance();
  if (!messaging) {
    console.warn(
      "[FCM] Notification listener not started: messaging unavailable."
    );
    return () => {};
  }

  const unsubForeground = onMessage(messaging, (payload) => {
    console.log("[FCM onMessage] foreground", payload);
    options.onNotification?.(payload);
  });
  cleanupFns.push(unsubForeground);

  if ("serviceWorker" in navigator) {
    try {
      await navigator.serviceWorker.ready;
    } catch (err) {
      console.warn("[FCM] serviceWorker.ready failed", err);
    }

    const swHandler = (event: MessageEvent) => {
      const data = event.data;
      if (!data || typeof data !== "object") return;

      if (data.type === "FCM_BACKGROUND_MESSAGE") {
        console.log("[FCM SW message] background", data.payload);
        options.onNotification?.(data.payload as MessagePayload);
      } else if (data.type === "FCM_NOTIFICATION_CLICK") {
        console.log("[FCM SW message] notification clicked", data.url);
        options.onNotificationClick?.(String(data.url ?? "/"));
      }
    };
    navigator.serviceWorker.addEventListener("message", swHandler);
    cleanupFns.push(() =>
      navigator.serviceWorker.removeEventListener("message", swHandler)
    );
  }

  return () => cleanupFns.forEach((fn) => fn());
};

// Kept for backward compatibility.
export const startForegroundMessageListener = (): Promise<() => void> =>
  startNotificationListener({});
